import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, of, Subject, takeUntil, tap } from 'rxjs';
import {
  ActiveProviderRequestInterface,
  ActiveProvidersInterface,
} from '../../interfaces/providers/active-providers';
import { environment } from '../../../../environments/environment';
import { StoreService } from '../../store/store.service';
import { GetByProviderTypeWithProviderId } from '../../interfaces/providers/getByProviderTypeWithProviderId';
import { GlobalMessageService } from '../global-services/global-message-service/global-message.service';
import { IdentityTypeEnum } from '../../enums/Providers/identity-type-enum';
import { BroadcastChannelService } from '../../broadcast-channel/services/broadcast-channel.service';
import { CompanyStatusEnum } from '../../enums/company/license-type';
import { IdentityEnum } from '../../enums/Providers/identity-enum';

@Injectable({
  providedIn: 'root',
})
export class ProviderService implements OnDestroy {
  private destroy$ = new Subject<void>();
  globalMessageService = inject(GlobalMessageService);
  broadcastChannelService = inject(BroadcastChannelService);

  constructor(private http: HttpClient, private storeService: StoreService) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  storeRefresh = () => {
    const subscription = this.getActiveProvidersRequest(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((f) => {
        subscription.unsubscribe();
      });
  };

  getActiveProvidersRequest = (sendMessage: boolean) => {
    return this.http
      .get<ActiveProviderRequestInterface>(
        `${environment.baseUrl}/providers/active`
      )
      .pipe(
        map((f) => {
          const items = f.Items;
          if (
            f.Items.length === 0 &&
            this.storeService.store$() &&
            this.storeService.store$().company &&
            this.storeService.store$().company.base &&
            this.storeService.store$().company.base?.CompanyStatus ===
              CompanyStatusEnum.Demo
          ) {

            const tempItems = [
              {
                Id: 'demoTrendyol',
                IdentityType: 1,
                Identity: IdentityEnum.Trendyol,
                IsActive: false,
                UpdateType: 0,

                Settings: [
                  {
                    Key: 'StoreName',
                    Value: 'Demo',
                  },
                ],
              },
              {
                Id: 'demoHepsiBurada',
                IdentityType: 1,
                Identity: IdentityEnum.HepsiBurada,
                IsActive: false,
                UpdateType: 0,
                Settings: [
                  {
                    Key: 'StoreName',
                    Value: 'Demo',
                  },
                ],
              },
              {
                Id: 'demoCicekSepeti',
                IdentityType: 1,
                Identity: IdentityEnum.CicekSepeti,
                IsActive: false,
                UpdateType: 0,
                Settings: [
                  {
                    Key: 'StoreName',
                    Value: 'Demo',
                  },
                ],
              },
            ];
            this.storeService.activeProviders$.set(tempItems);
          } else {

            items.map((item) => {
              const found = item.Settings.find(x=> x.Key === 'BuyboxIsActive')
              if (found) {
                item.BuyboxIsActive = found.Value === '1'
              }else
              {
                item.BuyboxIsActive = false;
              }
            })



            this.storeService.activeProviders$.set(items);
          }

          if (sendMessage) {
            this.broadcastChannelService.activeProviderChannelSend(items);
          }
          const result = f;
          result.Items = items;

          return result;
        }),
        catchError((error) => {
          return of({
            Items: [],
            Message: 'Aktif mağazalar alınırken bir hata oluştu',
          });
        })
      );
  };

  getByIdentityAndIdentityTypeV2 = (Identity: number, IdentityType: number) => {
    return this.http
      .post<{
        Message: string;
        Status: boolean;
        Providers: ActiveProvidersInterface[];
      }>(`${environment.baseUrl}/providers/getByIdentityAndIdentityTypeV2`, {
        Identity,
        IdentityType,
      })
      .pipe(
        catchError(() => {
          return of({
            Message: 'Mağaza bilgileri alınırken bir hata oluştu',
            Status: false,
            Providers: [],
          });
        })
      );
  };

  getByProviderTypeWithProviderId = (
    ProviderId: string,
    Identity?: number,
    IdentityType?: number
  ) => {
    return this.http
      .post<GetByProviderTypeWithProviderId>(
        `${environment.baseUrl}/provider/getByProviderTypeWithProviderId`,
        {
          ProviderId,
          Identity,
          IdentityType,
        }
      )
      .pipe(
        map((result) => {
          const found = result.Provider.Settings.find(x=> x.Key === 'BuyboxIsActive')
          if (found) {
            result.Provider.BuyboxIsActive = found.Value === '1'
          }else
          {
            result.Provider.BuyboxIsActive = false;
          }

          return result;
        }),
        catchError(() => {
          return of({
            Message: 'Mağaza detayları alınırken bir hata oluştu',
            Status: false,
            Provider: {} as ActiveProvidersInterface,
          } as GetByProviderTypeWithProviderId);
        })
      );
  };

  update = (providerSettings: ActiveProvidersInterface) => {
    const productUpdateType =
      providerSettings.Settings.find((f) => f.Key === 'ProductUpdateType')
        ?.Value ?? 0;
    if (productUpdateType) {
      providerSettings.UpdateType = +productUpdateType;
    }
    return this.http
      .post<{ Message: string; Status: boolean }>(
        `${environment.baseUrl}/providers/update`,
        providerSettings
      )
      .pipe(
        catchError(() => {
          return of({
            Message: 'Mağaza güncellenirken bir hata oluştu',
            Status: false,
          });
        }),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f && f.Status,
            summary: 'Mağaza Güncelle',
            infoDetail: `Mağaza güncelleme işlemi başarılı.`,
            errorDetail: `Mağaza güncelleme işlemi başarısız oldu. ${f.Message}`,
          });

          if (f && f.Status) {
            this.storeRefresh();
          }
        })
      );
  };

  add = (providerSettings: ActiveProvidersInterface) => {
    const productUpdateType =
      providerSettings.Settings.find((f) => f.Key === 'ProductUpdateType')
        ?.Value ?? 0;
    if (productUpdateType) {
      providerSettings.UpdateType = +productUpdateType;
    }
    return this.http
      .post<{ Message: string; Status: boolean }>(
        `${environment.baseUrl}/providers/add`,
        providerSettings
      )
      .pipe(
        catchError(() => {
          return of({
            Message: 'Mağaza eklenirken bir hata oluştu',
            Status: false,
          });
        }),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f && f.Status,
            summary: 'Mağaza Ekle',
            infoDetail: `Mağaza ekleme işlemi başarılı.`,
            errorDetail: `Mağaza ekleme işlemi başarısız oldu. ${f.Message}`,
          });
          if (f && f.Status) {
            this.storeRefresh();
          }
        })
      );
  };

  controlProvider = (Details: any, Identity: number, IdentityType: number) => {
    return this.http
      .post<{
        Details: any;
        Message: any;
        Result: any;
        Status: boolean;
      }>(`${environment.baseUrl}/providers/controlProvider`, {
        Details,
        Identity,
        IdentityType,
      })
      .pipe(
        map((f) => f),
        catchError(() => {
          return of({
            Details: [],
            Message: 'Mağaza kontrolü sırasında bir hata oluştu',
            Result: [],
            Status: false,
          });
        })
      );
  };

  changePassive = (
    Identity: number,
    IdentityType: IdentityTypeEnum,
    Id: any
  ) => {
    return this.http
      .post<{ Status: boolean; Message: string }>(
        `${environment.baseUrl}/providers/changePassive`,
        {
          Identity,
          IdentityType,
          Id,
        }
      )
      .pipe(
        catchError(() => {
          return of({
            Message: 'Mağaza pasife alınırken bir hata oluştu',
            Status: false,
          });
        }),
        tap((f) => {
          if (f && f.Status) {
            this.storeRefresh();
          }
        })
      );
  };
}
